// Enheter och användare (admin mockup v1 §8, v2 §5): enrol devices with a one-time QR,
// manage users with their roles and login methods.

import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import { Admin, Api, type SiteOut, type ApiKeySummary, getSession, type DeviceSummary, type RoleGrant, type UserSummary } from "../api/client.js";
import { t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";

const ROLES: RoleGrant["role"][] = ["operator", "lead", "quality", "administrator", "readonly"];

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "—");

export function DevicesUsers() {
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  // Integration keys (owner 2026-10-30): the adapter and scripts get their own tokens with roles;
  // the server's integrator token stays with the operator.
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [newKey, setNewKey] = useState<{ name: string; roles: string[]; days: string } | null>(null);
  const [issuedKey, setIssuedKey] = useState<{ name: string; token: string } | null>(null);
  const createKey = async () => {
    if (!newKey?.name.trim() || newKey.roles.length === 0) return;
    try {
      const r = await Admin.createApiKey(newKey.name, newKey.roles.map((role) => ({ role, siteId: null })), Number(newKey.days) || 0);
      setIssuedKey({ name: r.key.name, token: r.token });
      setNewKey(null);
      setKeys(await Admin.apiKeys());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const revokeKey = async (k: ApiKeySummary) => {
    if (!confirm(t("adm.du.key.revokeConfirm", { name: k.name }))) return;
    await Admin.revokeApiKey(k.id).catch((e: unknown) => setError(String(e)));
    setKeys(await Admin.apiKeys());
  };
  const [devices, setDevices] = useState<DeviceSummary[] | null>(null);
  const [editing, setEditing] = useState<Partial<UserSummary> & { password?: string; shortCode?: string; badge?: string } | null>(null);
  const [enrol, setEnrol] = useState<{ name: string; role: "handheld" | "mounted" | "workstation"; siteId?: string | null } | null>(null);
  const [sites, setSites] = useState<SiteOut[]>([]);
  const [queueNames, setQueueNames] = useState<string[]>([]);
  useEffect(() => { Admin.sites(false).then(setSites).catch(() => setSites([])); Api.bundle().then((b) => setQueueNames((b.siteConfig?.picking?.queues ?? []).map((q) => q.name))).catch(() => undefined); }, []);
  const QueueChips = ({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) => (
    <div className="achips">{queueNames.map((qn) => <button key={qn} type="button" className={`achip${value.includes(qn) ? " on" : ""}`} onClick={() => onChange(value.includes(qn) ? value.filter((x) => x !== qn) : [...value, qn])}>{qn}</button>)}{value.length === 0 && <span className="mut" style={{ fontSize: 11, alignSelf: "center" }}>{t("adm.du.queuesHint")}</span>}</div>
  );
  const [issued, setIssued] = useState<{ device: DeviceSummary; enrolUrl: string; expiresAt: string } | null>(null);
  useDetailHistory(!!(editing || enrol || issued || issuedKey), () => { setEditing(null); setEnrol(null); setIssued(null); setIssuedKey(null); });
  const [error, setError] = useState<string | null>(null);
  const [mfa, setMfa] = useState<{ email: string; otpauth: string; secret: string } | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const mfaCanvas = useRef<HTMLCanvasElement>(null);

  const reload = () => {
    Admin.users().then(setUsers).catch((e: unknown) => setError(String(e)));
    Admin.devices().then(setDevices).catch((e: unknown) => setError(String(e)));
    Admin.apiKeys().then(setKeys).catch(() => setKeys([]));
  };
  useEffect(reload, []);

  useEffect(() => {
    if (issued && canvas.current) void QRCode.toCanvas(canvas.current, new URL(issued.enrolUrl, location.origin).toString(), { width: 220, margin: 1 });
  }, [issued]);
  useEffect(() => {
    if (mfa && mfaCanvas.current) void QRCode.toCanvas(mfaCanvas.current, mfa.otpauth, { width: 180, margin: 1 });
  }, [mfa]);

  const reissue = async (d: DeviceSummary) => {
    if (d.enrolled && !window.confirm(t("adm.du.reenrolConfirm", { name: d.name }))) return;
    const r = await Admin.reissueDevice(d.id);
    setIssued({ device: d, enrolUrl: r.enrolUrl, expiresAt: r.expiresAt });
  };
  const remove = async (d: DeviceSummary) => {
    if (!window.confirm(d.enrolled ? t("adm.du.deactivateConfirm", { name: d.name }) : t("adm.du.removeConfirm", { name: d.name }))) return;
    await Admin.removeDevice(d.id);
    if (issued?.device.id === d.id) setIssued(null);
    reload();
  };

  const saveUser = async () => {
    if (!editing?.email || !editing.name) return;
    setError(null);
    try {
      await Admin.putUser({
        email: editing.email,
        name: editing.name,
        roles: editing.roles && editing.roles.length > 0 ? editing.roles : [{ role: "operator", siteId: null }],
        ...(editing.language ? { language: editing.language } : {}),
        homeSiteId: editing.homeSiteId ?? null,
        queues: editing.queues ?? [],
        ...(editing.theme !== undefined ? { theme: editing.theme } : {}),
        ...(editing.password ? { password: editing.password } : {}),
        ...(editing.shortCode !== undefined ? { shortCode: editing.shortCode || null } : {}),
        ...(editing.badge !== undefined ? { badge: editing.badge || null } : {}),
        ...(editing.active !== undefined ? { active: editing.active } : {}),
        ...(editing.loginMethod ? { loginMethod: editing.loginMethod } : {}),
      });
      setEditing(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const createDevice = async () => {
    if (!enrol?.name) return;
    setError(null);
    try {
      const r = await Admin.createDevice(enrol);
      setIssued({ device: r.device, enrolUrl: r.enrolUrl, expiresAt: r.expiresAt });
      setEnrol(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const startMfa = async (u: UserSummary) => {
    try {
      const r = await Admin.enrolMfa(u.id);
      setMfa({ email: u.email, otpauth: r.otpauth, secret: r.secret });
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleRole = (role: RoleGrant["role"]) => {
    const roles = editing?.roles ?? [];
    const has = roles.some((r) => r.role === role);
    setEditing({ ...editing, roles: has ? roles.filter((r) => r.role !== role) : [...roles, { role, siteId: null }] });
  };

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.devicesUsers")}</h1><div className="crumb">{devices ? t("adm.du.counts", { devices: devices.length, users: users?.length ?? 0 }) : ""}</div></div>
        <div className="right">
          <button type="button" className="abtn" onClick={() => setEditing({ roles: [{ role: "operator", siteId: null }], language: "sv", active: true })}>{t("adm.du.newUser")}</button>
          <button type="button" className="abtn primary" onClick={() => setEnrol({ name: "", role: "handheld" })}>{t("adm.du.enrolDevice")}</button>
        </div>
      </div>
      <div className="adm-content">
        {error && <p className="ahint" style={{ color: "var(--bad)" }}>{error}</p>}
        <div className={`adm-cols${!!(editing || enrol || issued || issuedKey) ? " detail-open" : ""}`}>
          <div className="adm-grow" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="card">
              <div className="hd">{t("adm.du.devices")}</div>
              <div className="bd flush">
                <table className="at">
                  <thead><tr><th>{t("adm.du.device")}</th><th>{t("adm.du.role")}</th><th>{t("adm.du.lastSeen")}</th><th>{t("adm.col.status")}</th><th></th></tr></thead>
                  <tbody>
                    {devices?.length === 0 && <tr><td colSpan={5} className="mut">{t("adm.du.noDevices")}</td></tr>}
                    {devices?.map((d) => (
                      <tr key={d.id}>
                        <td>{d.name}
                          {(sites.length > 1 || !d.siteId) && <div style={{ marginTop: 4 }}><select className="ain" style={{ height: 28, fontSize: 12 }} value={d.siteId ?? ""} onChange={(e) => void Admin.patchDevice(d.id, { siteId: e.target.value || null }).then(reload)}><option value="">{t("adm.du.device.noSite")}</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>}
                          {queueNames.length > 0 && <div style={{ marginTop: 4 }}><QueueChips value={d.queues} onChange={(v) => void Admin.patchDevice(d.id, { queues: v }).then(reload)} /></div>}</td>
                        <td>{t(`adm.du.role.${d.role}` as "adm.du.role.handheld")}</td>
                        <td className="id mut">{fmtWhen(d.lastSeenAt)}</td>
                        <td>{!d.active ? <span className="pill mut">{t("adm.du.inactive")}</span> : d.enrolled ? <span className="pill ok">{t("adm.du.enrolled")}</span> : d.enrolPending ? <span className="pill warn">{t("adm.du.pending")}</span> : <span className="pill mut">{t("adm.du.expired")}</span>}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {d.active && <button type="button" className="abtn sm" onClick={() => void reissue(d)}>{d.enrolled ? t("adm.du.reenrol") : t("adm.du.newCode")}</button>}{" "}
                          {d.active && <button type="button" className="abtn sm danger" onClick={() => void remove(d)}>{d.enrolled ? t("adm.du.deactivate") : t("adm.du.remove")}</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card">
              <div className="hd">{t("adm.du.keys")} <span className="sub">{t("adm.du.keys.sub")}</span></div>
              <div className="bd flush">
                <table className="at">
                  <thead><tr><th>{t("adm.du.name")}</th><th>{t("adm.du.roles")}</th><th>{t("adm.du.key.lastUsed")}</th><th>{t("adm.col.status")}</th><th></th></tr></thead>
                  <tbody>
                    {keys.length === 0 && <tr><td colSpan={5} className="mut">{t("adm.du.keys.none")}</td></tr>}
                    {keys.map((k) => (
                      <tr key={k.id}>
                        <td><b>{k.name}</b><div className="mut" style={{ fontSize: 12 }}>{t("adm.du.key.created", { by: k.createdBy ?? "—", at: fmtWhen(k.createdAt) })}</div></td>
                        <td>{k.roles.map((r) => t(`adm.role.${r.role}` as "adm.role.operator")).join(", ")}</td>
                        <td className="id mut">{fmtWhen(k.lastUsedAt)}<div style={{ fontSize: 12 }}>{k.expiresAt ? (new Date(k.expiresAt).getTime() < Date.now() ? <span className="pill bad">{t("adm.du.key.expired", { at: fmtWhen(k.expiresAt) })}</span> : t("adm.du.key.expires", { at: fmtWhen(k.expiresAt) })) : t("adm.du.key.noExpiry")}</div></td>
                        <td>{k.active ? <span className="pill ok">{t("adm.du.key.active")}</span> : <span className="pill mut">{t("adm.du.key.revoked")}</span>}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{k.active && <button type="button" className="abtn sm danger" onClick={() => void revokeKey(k)}>{t("adm.du.key.revoke")}</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bd" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {newKey === null ? (
                  <button type="button" className="abtn sm" onClick={() => setNewKey({ name: "", roles: ["integrator"], days: "365" })}>{t("adm.du.key.new")}</button>
                ) : (
                  <form style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); void createKey(); }}>
                    <input className="ain" style={{ height: 30, width: 200 }} value={newKey.name} onChange={(e) => setNewKey({ ...newKey, name: e.target.value })} placeholder={t("adm.du.key.namePlaceholder")} autoFocus />
                    <div className="achips">{(["integrator", "readonly", "lead", "administrator"] as const).map((r) => <button key={r} type="button" className={`achip${newKey.roles.includes(r) ? " on" : ""}`} onClick={() => setNewKey({ ...newKey, roles: newKey.roles.includes(r) ? newKey.roles.filter((x) => x !== r) : [...newKey.roles, r] })}>{t(`adm.role.${r}` as "adm.role.operator")}</button>)}</div>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>{t("adm.du.key.validDays")} <input className="ain" style={{ height: 30, width: 70 }} inputMode="numeric" value={newKey.days} onChange={(e) => setNewKey({ ...newKey, days: e.target.value.replace(/[^0-9]/g, "") })} /></label>
                    <button type="submit" className="abtn sm primary" disabled={!newKey.name.trim() || newKey.roles.length === 0}>{t("adm.du.key.create")}</button>
                    <button type="button" className="abtn sm" onClick={() => setNewKey(null)}>{t("common.cancel")}</button>
                  </form>
                )}
                {issuedKey && (
                  <span className="ahint" style={{ margin: 0 }}>
                    {t("adm.du.key.issued", { name: issuedKey.name })} <code style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, userSelect: "all" }}>{issuedKey.token}</code>
                  </span>
                )}
              </div>
            </div>
            <div className="card">
              <div className="hd">{t("adm.du.users")}</div>
              <div className="bd flush">
                <table className="at">
                  <thead><tr><th>{t("adm.du.name")}</th><th>{t("adm.du.roles")}</th><th>{t("adm.du.login")}</th><th>{t("adm.col.status")}</th><th></th></tr></thead>
                  <tbody>
                    {users?.map((u) => (
                      <tr key={u.id} className="click" onClick={() => setEditing({ ...u })}>
                        <td>{u.name}<div className="mut">{u.email}</div></td>
                        <td>{u.roles.map((r) => t(`adm.role.${r.role}` as "adm.role.operator")).join(" · ")}</td>
                        <td>{[u.loginMethod === "entra" ? "Microsoft Entra ID" : u.methods.password ? (u.methods.mfa ? t("adm.du.m.passwordMfa") : t("adm.du.m.password")) : null, u.methods.shortCode ? t("adm.du.m.shortCode") : null, u.methods.badge ? t("adm.du.m.badge") : null].filter(Boolean).join(" · ") || "—"}</td>
                        <td>{u.active ? <span className="pill ok">{t("adm.du.active")}</span> : <span className="pill mut">{t("adm.du.inactive")}</span>}</td>
                        <td>{u.methods.password && !u.methods.mfa && <button type="button" className="abtn sm" onClick={(e) => { e.stopPropagation(); void startMfa(u); }}>{t("adm.du.enrolMfa")}</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="adm-w460 adm-detail adm-detail-only" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {!!(editing || enrol || issued || issuedKey) && <DetailBar title={editing ? (editing.name || t("adm.du.newUser")) : enrol ? (enrol.name || t("adm.du.enrolDevice")) : issued ? issued.device.name : issuedKey ? issuedKey.name : ""} sub={undefined} onBack={() => { setEditing(null); setEnrol(null); setIssued(null); setIssuedKey(null); }} />}
            {enrol && (
              <div className="card">
                <div className="hd">{t("adm.du.enrolDevice")}</div>
                <div className="bd aform">
                  <span>{t("adm.du.name")}</span><input className="ain" value={enrol.name} onChange={(e) => setEnrol({ ...enrol, name: e.target.value })} placeholder="iPhone Lager 2" autoFocus />
                  {sites.length > 1 && <><span>{t("adm.du.homeSite")}</span><select className="ain" value={enrol.siteId ?? ""} onChange={(e) => setEnrol({ ...enrol, siteId: e.target.value || null })}><option value="">{t("adm.du.noHomeSite")}</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></>}
                  <span>{t("adm.du.role")}</span>
                  <div className="achips">{(["handheld", "mounted", "workstation"] as const).map((r) => <button key={r} type="button" className={`achip${enrol.role === r ? " on" : ""}`} onClick={() => setEnrol({ ...enrol, role: r })}>{t(`adm.du.role.${r}`)}</button>)}</div>
                  <span></span><div style={{ display: "flex", gap: 8 }}><button type="button" className="abtn primary" disabled={!enrol.name.trim()} onClick={() => void createDevice()}>{t("adm.du.issueCode")}</button><button type="button" className="abtn" onClick={() => setEnrol(null)}>{t("common.cancel")}</button></div>
                </div>
              </div>
            )}
            {issued && (
              <div className="card">
                <div className="hd">{issued.device.name} <span className="sub">{t("adm.du.codeValid", { until: new Date(issued.expiresAt).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) })}</span></div>
                <div className="bd qrbox"><canvas ref={canvas} /><div><p className="ahint">{t("adm.du.howToEnrol")}</p><p className="ahint" style={{ marginTop: 10 }}>{t("adm.du.codeText")}</p><p className="id" style={{ fontFamily: "var(--mono)", fontSize: 26, fontWeight: 700, letterSpacing: "0.08em", margin: "4px 0 0", userSelect: "all" }}>{new URL(issued.enrolUrl, location.origin).searchParams.get("enrol")}</p></div></div>
                <div className="bd"><button type="button" className="abtn sm" onClick={() => setIssued(null)}>{t("common.close")}</button></div>
              </div>
            )}
            {mfa && (
              <div className="card">
                <div className="hd">{t("adm.du.mfaTitle")} <span className="sub">{mfa.email}</span></div>
                <div className="bd qrbox"><canvas ref={mfaCanvas} /><p className="ahint">{t("adm.du.mfaHow")}<br /><code>{mfa.secret}</code></p></div>
                <div className="bd"><button type="button" className="abtn sm" onClick={() => setMfa(null)}>{t("common.close")}</button></div>
              </div>
            )}
            {editing && (
              <div className="card">
                <div className="hd">{editing.id ? editing.name : t("adm.du.newUser")}</div>
                <div className="bd aform">
                  <span>{t("adm.du.name")}</span><input className="ain" value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus={!editing.id} />
                  {queueNames.length > 0 && <><span>{t("adm.du.queues")}</span><QueueChips value={editing.queues ?? []} onChange={(v) => setEditing({ ...editing, queues: v })} /></>}
                  {sites.length > 1 && <><span>{t("adm.du.homeSite")}</span><select className="ain" value={editing.homeSiteId ?? ""} onChange={(e) => setEditing({ ...editing, homeSiteId: e.target.value || null })}><option value="">{t("adm.du.noHomeSite")}</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></>}
                  <span>{t("login.email")}</span><input className="ain mono" value={editing.email ?? ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} disabled={Boolean(editing.id)} />
                  <span>{t("adm.du.roles")}</span>
                  <div className="achips">{ROLES.map((r) => <button key={r} type="button" className={`achip${editing.roles?.some((g) => g.role === r) ? " on" : ""}`} onClick={() => toggleRole(r)}>{t(`adm.role.${r}` as "adm.role.operator")}</button>)}</div>
                  <span>{t("adm.du.login")}</span>
                  <div className="achips">{(["password", "entra"] as const).map((m) => <button key={m} type="button" className={`achip${(editing.loginMethod ?? "password") === m ? " on" : ""}`} onClick={() => setEditing({ ...editing, loginMethod: m })}>{t(`adm.du.lm.${m}`)}</button>)}</div>
                  <span></span><p className="ahint">{t("adm.du.lm.hint")}</p>
                  {(editing.loginMethod ?? "password") === "password" && <><span>{t("adm.du.password")}</span><input className="ain" type="password" value={editing.password ?? ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder={editing.methods?.password ? t("adm.du.keep") : t("adm.du.min10")} autoComplete="new-password" />
                  </>}
                  <span>{t("adm.du.shortCode")}</span><input className="ain mono" inputMode="numeric" value={editing.shortCode ?? ""} onChange={(e) => setEditing({ ...editing, shortCode: e.target.value.replace(/\D/g, "") })} placeholder={editing.methods?.shortCode ? t("adm.du.keep") : "4–8 siffror"} />
                  <span>{t("adm.du.badge")}</span><input className="ain mono" value={editing.badge ?? ""} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} placeholder={editing.methods?.badge ? t("adm.du.keep") : t("adm.du.badgeHint")} />
                  <span>{t("settings.lang")}</span>
                  <select value={editing.language ?? "sv"} onChange={(e) => setEditing({ ...editing, language: e.target.value })}><option value="sv">Svenska</option><option value="en">English</option></select>
                  <span>{t("settings.theme")}</span>
                  <select value={editing.theme ?? ""} onChange={(e) => setEditing({ ...editing, theme: (e.target.value || null) as "dark" | "light" | "auto" | null })}><option value="">{t("settings.theme.device")}</option><option value="dark">{t("settings.theme.dark")}</option><option value="light">{t("settings.theme.light")}</option><option value="auto">{t("settings.theme.auto")}</option></select>
                  <span>{t("adm.col.status")}</span>
                  <div className="achips"><button type="button" className={`achip${editing.active !== false ? " on" : ""}`} onClick={() => setEditing({ ...editing, active: true })}>{t("adm.du.active")}</button><button type="button" className={`achip${editing.active === false ? " on" : ""}`} onClick={() => setEditing({ ...editing, active: false })} disabled={editing.id === getSession()?.userId}>{t("adm.du.inactive")}</button></div>
                  <span></span><div style={{ display: "flex", gap: 8 }}><button type="button" className="abtn primary" disabled={!editing.email?.includes("@") || !editing.name?.trim() || (!!editing.password && editing.password.length < 10)} onClick={() => void saveUser()}>{t("settings.save")}</button><button type="button" className="abtn" onClick={() => setEditing(null)}>{t("common.cancel")}</button></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
